import {useMemo} from "react";
import {useQuery} from "@tanstack/react-query";

import {POOL_CONFIG} from "@/src/modules/amenities/lib/amenity-config";
import {getPropertyAmenityService} from "@/src/modules/amenities/lib/registry";
import type {PropertyAmenity} from "@/src/modules/amenities/types";
import useResidentHouseholdId from "@/src/modules/core/hooks/use-resident-household-id";
import {meService} from "@/src/modules/core/services/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {RESIDENT_HOME_AMENITIES_PREVIEW_LIMIT} from "@/src/modules/home/lib/resident-home-block-layout";

const EMPTY_PROPERTY_AMENITIES: PropertyAmenity[] = [];

type UseResidentHomePropertyAmenitiesOptions = {
  enabled?: boolean;
  limit?: number;
};

export function useResidentHomePropertyAmenities({
  enabled = true,
  limit = RESIDENT_HOME_AMENITIES_PREVIEW_LIMIT,
}: UseResidentHomePropertyAmenitiesOptions = {}) {
  const userProperty = useStoredUserProperty();
  const householdId = useResidentHouseholdId();
  const canShow = meService.getHasPermission("property_amenity show");
  const service = getPropertyAmenityService(POOL_CONFIG);

  const filter = useMemo(
    () =>
      userProperty?.id && householdId
        ? {context_property_id: userProperty.id, household_id: householdId}
        : {},
    [householdId, userProperty?.id],
  );

  const options = {
    page: {limit, offset: 0},
    sort: "amenity_type,name",
    filter,
  };

  const query = useQuery({
    queryKey: ["resident-home-property-amenities", limit, filter],
    queryFn: () => service.getPropertyAmenities(options),
    enabled: enabled && canShow && Boolean(userProperty?.id && householdId),
  });

  const amenities =
    query.isSuccess && query.data ? (query.data.data as PropertyAmenity[]) : EMPTY_PROPERTY_AMENITIES;
  const totalRecords = query.data?.meta?.total ?? 0;

  return {
    amenities,
    totalRecords,
    canShow,
    ...query,
  };
}
